<script setup>
import { computed, ref, onBeforeMount, onMounted, watch, nextTick } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import _ from 'lodash';
import DynamicService from '@/services/DynamicService';
import ModalBoxAlert from '@/components/modals/ModalBoxAlert.vue';
import PaywallService from '@/services/PaywallService'
import FormButton from '../../components/Atoms/FormButton.vue';
import FormUpload from '../../components/Atoms/FormUpload.vue';
import DropDownMenu from '../../components/Atoms/DropDownMenu.vue';
import DropDownMenuItem from '../../components/Atoms/DropDownMenuItem.vue';
import IconBase from '../../components/icon/IconBase.vue';
import IconImage from '../../components/icon/IconImage.vue';
import IconChevronUpDown from '../../components/icon/IconChevronUpDown.vue';
import Alert from '../../components/Atoms/Alert.vue';
import FormSwitch from '../../components/Atoms/FormSwitch.vue';
import FormInput from '../../components/Atoms/FormInput.vue';
import FormText from '../../components/Atoms/FormText.vue';
import Tooltip from '@/components/Atoms/Tooltip.vue';
import Collapse from '../../components/Atoms/Collapse.vue';
import TabGroup from '../Atoms/TabGroup.vue';
import TabItem from '../Atoms/TabItem.vue';
import IconBin from '../icon/IconBin.vue';
import IconInfo from '../icon/IconInfo.vue';

const { notify } = useNotification()
const store = useStore()
const route = useRoute()

// Refs
const watermarkPositionDropdown = ref(null)
const collapseOpen = ref({
	media: false,
	ga: false,
})

const COLLAPSE_KEYS = ['media', 'ga']

const WATERMARK_POSITION_TITLES = {
	tr: 'Top Right',
	tl: 'Top Left',
	cc: 'Center',
	bl: 'Bottom Left',
	br: 'Bottom Right',
}

const isFeatureActive = (feature) => {
	if (feature === 'ga') {
		return features.value.ga.enabled
	}
	return false
}

const features = ref({
	ga: {
		enabled: false,
		value: null,
		valueType: 'string'
	},
	embedAutoplay: {
		error: false,
		enabled: true,
		value: null,
		valueType: 'bool'
	},
	dynamicPlayerCaptionsEnabled: {
		enabled: false,
		valueType: 'bool',
	},
	embedPoster: {
		error: false,
		enabled: false,
		value: null,
		valueType: 'string'
	},
	embedWatermark: {
		error: false,
		enabled: false,
		value: null,
		valueType: 'string'
	},
	embedWatermarkPosition: {
		error: false,
		enabled: true,
		value: 'tr',
		title: 'Top Right',
		valueType: 'string'
	},
	embedStreamOfflineMessage: {
		error: false,
		errorMsg: '',
		enabled: false,
		value: 'Stream Offline',
		prevValue: 'Stream Offline',
		valueType: 'string'
	},
})

const disabledSaveGA = ref(true)

const featureProcessing = ref({
	ga: false,
	embedAutoplay: false,
	dynamicPlayerCaptionsEnabled: false,
	embedPoster: false,
	embedWatermark: false,
	embedWatermarkPosition: false,
	embedStreamOfflineMessage: false
})

const featureDisabled = ref({
	embedStreamOfflineMessage: true,
})

const maxPosterUploadMB = ref(2)
const embedWatermarkTemp = ref(null)
const embedPosterTemp = ref(null)
const selectedPosterFileName = ref(null)
const selectedWatermarkFileName = ref(null)
const activeTab = ref('poster')
const uploadAlertVisible = ref(false)
const uploadAlertMessage = ref('')
// Computed
const player = computed(() => store.getters['DynamicPlayers/dynamicPlayer'])
const hasSource = computed(() => !!(player.value?.source || player.value?.subSource))

const canSaveWatermarkPosition = computed(() => {
	return (
		features.value.embedWatermark.value &&
		features.value.embedWatermarkPosition.value !==
		player.value.metadata.embedWatermarkPosition
	);
})

const posterUrl = computed(() => {
	if (embedPosterTemp.value) return embedPosterTemp.value
	const imageId = player.value?.metadata?.embedPoster;
	return imageId ? `https://assets.castr.io/embedImages/${imageId}` : null;
})

const watermarkUrl = computed(() => {
	if (embedWatermarkTemp.value) return embedWatermarkTemp.value;
	const imageId = player.value.metadata?.embedWatermark;
	return imageId ? `https://assets.castr.io/embedImages/${imageId}` : null;
})

watch(() => route.params.playerId, async (newId) => {
	// set default values on switch 
	features.value.embedStreamOfflineMessage = {
		error: false,
		errorMsg: '',
		enabled: false,
		value: 'Stream Offline',
		prevValue: 'Stream Offline',
		valueType: 'string'
	},
		handleDynamicStreamSwitch()
})

watch(hasSource, (val) => {
	if (!val && features.value.dynamicPlayerCaptionsEnabled.enabled) {
		features.value.dynamicPlayerCaptionsEnabled.enabled = false;
		saveSetting('dynamicPlayerCaptionsEnabled', false);
	}
})

onBeforeMount(async () => {
	handleDynamicStreamSwitch()
})

const handleDynamicStreamSwitch = async () => {
	try {
		await store.dispatch('DynamicPlayers/getPlayer', route.params?.playerId)
	} catch (err) { }

	_.forIn(player.value.metadata, (value, key) => {
		if (value !== undefined && value !== null && key in features.value) {
			let enabled = value !== false;
			if (Array.isArray(enabled)) {
				enabled = enabled.length;
			}

			if (key === 'embedStreamOfflineMessage') {
				features.value[key].prevValue = value === false ? 'Stream Offline' : value;
			}

			features.value[key].enabled = enabled;
			if (value.constructor !== Boolean) {
				features.value[key].value = value;
			}
		}
	})

	const watermarkPos = features.value.embedWatermarkPosition.value
	features.value.embedWatermarkPosition.title =
		WATERMARK_POSITION_TITLES[watermarkPos] || 'Top Right'
}

// Methods
const showUploadAlert = (message) => {
	uploadAlertMessage.value = message
	nextTick(() => {
		uploadAlertVisible.value = true
	})
}

const clearFileInput = (name) => {
	const selector = name === 'watermark' ? '#embed-watermark-input' : '#embed-poster-input'
	const imageInput = document.querySelector(selector)
	if (imageInput) {
		imageInput.value = null
	}
}

const onEmbedPosterPreview = (file, name) => {
	if (!file) return

	if (file.type.indexOf('image/') === -1) {
		showUploadAlert('Only valid image files are allowed. Please pick an image file')
		clearFileInput(name)
		if (name === 'watermark') {
			selectedWatermarkFileName.value = null
		} else {
			selectedPosterFileName.value = null
		}
		return
	}

	let maxSize = name === 'watermark' ? 1 : 2
	maxPosterUploadMB.value = maxSize

	maxSize *= 1024 * 1024

	if (file.size > maxSize) {
		showUploadAlert(`Uploaded image too large. Image must not exceed ${maxPosterUploadMB.value}MB`)
		clearFileInput(name)
		if (name === 'watermark') {
			selectedWatermarkFileName.value = null
		} else {
			selectedPosterFileName.value = null
		}
		return
	}

	const validImageTypes = name === 'watermark' ? ['png', 'jpeg', 'gif', 'avif'] : null
	const uploadedImageFileType = file.type.replace('image/', '')
	if (
		validImageTypes &&
		validImageTypes.indexOf(uploadedImageFileType) === -1
	) {
		showUploadAlert('Only PNG/JPG/GIF/AVIF image files are allowed')
		clearFileInput(name)
		if (name === 'watermark') {
			selectedWatermarkFileName.value = null
		} else {
			selectedPosterFileName.value = null
		}
		return
	}

	if (name === 'watermark') {
		selectedWatermarkFileName.value = file.name
	} else {
		selectedPosterFileName.value = file.name
	}

	imageReader(file, base64 => {
		if (name === 'watermark') {
			embedWatermarkTemp.value = base64;
		} else {
			embedPosterTemp.value = base64;
		}
	})
}

const saveEmbedPoster = async (type) => {
	const featureProp = type === 'watermark' ? 'embedWatermark' : 'embedPoster';
	features.value[featureProp].error = false;
	const selector = type === 'watermark' ? '#embed-watermark-input' : '#embed-poster-input';
	const imageInput = document.querySelector(selector);
	const imageFile = imageInput.files[0];
	if (!imageInput.value || !imageFile) {
		features.value[featureProp].error = 'please pick an image file';
		return;
	}
	// this.featureProcessing.embedPoster = true;
	featureProcessing.value[featureProp] = true;
	// -- upload reuqest --
	const fdata = new FormData();
	fdata.append('file', imageFile);
	const res = await DynamicService.uploadPlayerPoster(
		player.value._id,
		fdata,
		type === 'watermark'
	);
	if (res.success) {
		if (type === 'watermark') {
			await saveWatermarkPosition();
			embedWatermarkTemp.value = null;
			selectedWatermarkFileName.value = null;
		} else {
			const poster = `https://assets.castr.io/embedImages/${res.uploadId}`;
			PaywallService.updateStreamPaywall(player.value._id, { wallpaper_url: poster }).catch(err => {
				console.error('update paywall stream poster failed: ', err)
			});
			embedPosterTemp.value = null;
			selectedPosterFileName.value = null;
		}
		features.value[featureProp].value = res.uploadId;
		await store.dispatch('DynamicPlayers/getPlayer', route.params?.playerId);
		notify({
			group: 'success',
			text: `${type === 'watermark' ? 'Watermark' : 'Poster'} uploaded successfully.`
		});
		imageInput.value = ''
	} else {
		features.value[featureProp].error = 'Unable to upload the image. Please try again later.';
	}

	featureProcessing.value[featureProp] = false;
}

const cancelUpload = async (type) => {
	const selector = type === 'watermark' ? '#embed-watermark-input' : '#embed-poster-input';
	const previewFlag = type === 'watermark' ? embedWatermarkTemp.value : embedPosterTemp.value;
	if (previewFlag) {
		if (type === 'watermark') {
			embedWatermarkTemp.value = null;
			selectedWatermarkFileName.value = null;
		} else {
			embedPosterTemp.value = null;
			selectedPosterFileName.value = null;
			features.value.embedPoster.error = false;
		}
		document.querySelector(selector).value = null;
		return;
	}
	removePoster(type);
}

const removePoster = async (type) => {
	try {
		const settingsProp =
			type === 'watermark' ? 'embedWatermark' : 'embedPoster';
		await saveSetting(settingsProp, null);
		features.value[settingsProp].value = null;
		if (type === 'watermark') {
			selectedWatermarkFileName.value = null;
		} else {
			selectedPosterFileName.value = null;
		}
		await store.dispatch('DynamicPlayers/getPlayer', route.params?.playerId);
	} catch (e) {
		console.log(e);
	}
}

const saveWatermarkPosition = async () => {
	const newpos = features.value.embedWatermarkPosition.value;
	await saveSetting('embedWatermarkPosition', newpos);
	await store.dispatch('DynamicPlayers/getPlayer', route.params?.playerId);
}

const saveSetting = async (key, value, successToast = false) => {
	featureProcessing.value[key] = true;
	try {
		await DynamicService.savePlayerMetadata(player.value._id, key, value);
		// clear stream meta cache in embed player
		DynamicService.clearPlayerMetadataCache(player.value._id, player.value.key)
		if (successToast) {
			notify({ group: 'success', text: 'Setting saved' })
		}
	} catch (e) {
		notify({ group: 'error', text: 'Unable to save your changes.' });
	}
	featureProcessing.value[key] = false;
	disabledSaveGA.value = true;
}

function imageReader(file, cb) {
	if (!file) {
		return;
	}

	var reader = new FileReader();
	reader.onload = function () {
		if (cb) {
			cb(reader.result);
		}
	};

	reader.readAsDataURL(file);
}

const setWatermarkPosition = (value, title) => {
	features.value.embedWatermarkPosition.value = value
	features.value.embedWatermarkPosition.title = title
	watermarkPositionDropdown.value.toggleDropdown()
}

const savePlayerGAId = async () => {
	const gaId = features.value.ga.value;
	if (!gaId) return;
	await saveSetting('ga', gaId);
}

const enableSave = (feature) => featureDisabled.value[feature] = false;
const isDisabled = (option) => {
	if (featureProcessing.value[option]) return true
	if (featureDisabled.value[option]) return true

	if (option === 'embedStreamOfflineMessage') {
		if (features.value[option].value === '') {
			features.value[option].error = true
			features.value[option].errorMsg = 'Can\'t be empty'
			return true
		}

		if ((features.value[option].value || '').length > 50) {
			features.value[option].error = true
			features.value[option].errorMsg = 'Offline message can\'t exceeds 50 characters'
			return true
		} else {
			features.value[option].error = false
			features.value[option].errorMsg = ''
		}

		if (features.value[option].prevValue === features.value[option].value) {
			return true
		}
	}
	return false
}

const toggleFeature = async (featureName, newFeature = false) => {
	if (featureProcessing.value[featureName]) return;
	const feature = features.value[featureName];
	if (!feature) return;
	let nstate = feature.enabled;
	feature.enabled = nstate;
	if (feature.valueType === 'bool' || !nstate) {
		if (feature.valueType === 'array' && !nstate) {
			nstate = [];
		}
		await saveSetting(featureName, nstate, true);
	}

	if (featureName === 'embedStreamOfflineMessage' && !nstate) {
		features.value[featureName].value = 'Stream Offline'
		features.value[featureName].prevValue = 'Stream Offline'
	}
}

const setStreamOfflineMessage = async () => {
	const msg = features.value.embedStreamOfflineMessage.value;
	await saveSetting('embedStreamOfflineMessage', msg, true);
	features.value.embedStreamOfflineMessage.prevValue = msg
}

const setActiveTab = (tab) => {
	activeTab.value = tab
}
const syncCollapseOpen = () => {
	COLLAPSE_KEYS.forEach((key) => {
		collapseOpen.value[key] = isFeatureActive(key)
	})
}

COLLAPSE_KEYS.forEach((key) => {
	watch(() => isFeatureActive(key), (active) => {
		if (active) collapseOpen.value[key] = true
	})
})

onMounted(() => {
	syncCollapseOpen()
})

</script>

<template>
	<div class="bg-surface-2 rounded-xl shadow-[0_2px_6px_rgba(0,0,0,0.28)] p-4 md:p-5 !mb-4">
		<h4 class="text-base font-medium text-surface-text-2 mb-2">Interface</h4>
		<section class="grid grid-cols-1 sm:grid-cols-2 grid-rows-[1fr_auto] bg-surface-3 !p-3 !rounded-lg !mb-3">
			<div>
				<h4 class="text-tiny font-medium text-surface-text-2 !mb-1">Offline Message</h4>
				<p class="text-xs text-surface-7">Displayed when the stream is offline</p>
			</div>
			<FormSwitch class="justify-self-start sm:justify-self-end self-center" @change="toggleFeature('embedStreamOfflineMessage')"
				v-model="features.embedStreamOfflineMessage.enabled" :disabled="featureProcessing.embedStreamOfflineMessage" />
			<div class="sm:col-span-2" v-if="features.embedStreamOfflineMessage.enabled">
				<div class="flex flex-col items-center !mt-2 mb-2">
					<FormText v-model="features.embedStreamOfflineMessage.value"
						:helper="features.embedStreamOfflineMessage.errorMsg" :is-error="!!features.embedStreamOfflineMessage.error"
						custom-classes="bg-surface-4" @input="enableSave('embedStreamOfflineMessage')" />
				</div>
				<FormButton :disabled="isDisabled('embedStreamOfflineMessage')" @click="setStreamOfflineMessage">
					{{ featureProcessing.embedStreamOfflineMessage ? 'Saving' : 'Save' }}
				</FormButton>
			</div>
		</section>
		<section class="grid grid-cols-1 sm:grid-cols-2 grid-rows-[1fr_auto] bg-surface-3 !p-3 !rounded-lg !mb-3">
			<div>
				<h4 class="text-tiny font-medium text-surface-text-2 !mb-1">Captions</h4>
				<p class="text-xs text-surface-7">Captions will be shown if they are available in the source content.</p>
			</div>
			<Tooltip wrapperClass="justify-self-start sm:justify-self-end self-center" :active="!hasSource" content="Select a source first">
				<FormSwitch v-model="features.dynamicPlayerCaptionsEnabled.enabled"
					:disabled="featureProcessing.dynamicPlayerCaptionsEnabled || !hasSource"
					@change="toggleFeature('dynamicPlayerCaptionsEnabled')" />
			</Tooltip>
		</section>
		<Collapse class="!mb-3" v-model:is-open="collapseOpen.media">
			<template #header>
				<div class="flex items-center gap-x-2 text-surface-text-1 text-tiny font-medium">
					<icon-base class="shrink-0" :width="20" :height="20" :widthViewBox="24" :heightViewBox="24">
						<icon-image />
					</icon-base>
					<h4 class="text-tiny font-medium text-surface-text-2">Media Assets</h4>
				</div>
			</template>
			<template #content>
				<TabGroup class="grid grid-cols-2 !mb-4 bg-surface-4">
					<TabItem type="buttons-3" centered label="Custom Embed Poster" :isActive="activeTab === 'poster'"
						@click="setActiveTab('poster')" />
					<TabItem type="buttons-3" centered label="Watermark" :isActive="activeTab === 'watermark'"
						@click="setActiveTab('watermark')" />
				</TabGroup>
				<section v-if="activeTab === 'poster'" class="flex flex-col gap-3">
					<div class="flex items-center gap-2 min-w-0 w-full">
						<img v-if="posterUrl" width="100px" :src="posterUrl" class="w-24 shrink-0 object-contain rounded" />
						<span v-if="selectedPosterFileName" class="text-xs text-surface-8 truncate max-w-48"
							:title="selectedPosterFileName">{{ selectedPosterFileName }}</span>
						<ul v-if="!posterUrl">
							<li class="text-xs text-surface-8">Shown before the player loads</li>
							<li class="text-xs text-surface-8">File types: PNG, JPG, WebP, AVIF, APNG, GIF</li>
							<li class="text-xs text-surface-8">Size: 2MB max</li>
						</ul>
						<FormButton is-icon type="link" size="sm" class="ml-auto shrink-0"
							v-if="embedPosterTemp || features.embedPoster.value || player.metadata?.embedPoster"
							:disabled="featureProcessing.embedPoster" @click="cancelUpload">
							<icon-base>
								<icon-bin />
							</icon-base>
						</FormButton>
					</div>
					<div class="flex flex-col items-stretch gap-2 w-full sm:flex-row sm:items-center">
						<FormUpload size="sm" name="poster" id="embed-poster-input" placeholder="No file chosen" type="secondary"
							:label="embedPosterTemp || player.metadata?.embedPoster ? 'Change' : 'Upload poster'"
							:on-file-change="onEmbedPosterPreview" :show-icon="true" />
						<FormButton size="sm" v-if="embedPosterTemp" :disabled="featureProcessing.embedPoster"
							@click="saveEmbedPoster('poster')">
							{{ featureProcessing.embedPoster ? 'Saving' : 'Save Poster' }}
						</FormButton>
						<p class="text-xs text-surface-8 sm:ml-auto">640×360 recommended</p>
					</div>
					<Alert v-show="features.embedPoster.error" :text="features.embedPoster.error" />
				</section>
				<section v-if="activeTab === 'watermark'" class="flex flex-col gap-3">
					<div class="flex items-center gap-2 min-w-0 w-full">
						<img v-if="watermarkUrl" width="100px" :src="watermarkUrl" class="w-24 shrink-0 object-contain rounded" />
						<span v-if="selectedWatermarkFileName" class="text-xs text-surface-8 truncate max-w-48"
							:title="selectedWatermarkFileName">{{ selectedWatermarkFileName }}</span>
						<ul v-if="!watermarkUrl">
							<li class="text-xs text-surface-8">File types: PNG, JPEG, AVIF or GIF</li>
							<li class="text-xs text-surface-8">Size: 2MB max</li>
							<li class="text-xs text-surface-8">640x360 recommended</li>
						</ul>
						<FormButton is-icon type="link" size="sm" class="ml-auto shrink-0"
							v-if="embedWatermarkTemp || features.embedWatermark.value || player.metadata?.embedWatermark"
							:disabled="featureProcessing.embedWatermark" @click="cancelUpload('watermark')">
							<icon-base>
								<icon-bin />
							</icon-base>
						</FormButton>
					</div>
					<div class="flex flex-col items-stretch gap-2 w-full sm:flex-row sm:items-center">
						<DropDownMenu type="outline" ref="watermarkPositionDropdown">
							<template #toggle-button>
								{{ features.embedWatermarkPosition.title }}
								<icon-base class="!ml-1">
									<icon-chevron-up-down />
								</icon-base>
							</template>
							<template #menu-items>
								<DropDownMenuItem class="uppercase" @click="setWatermarkPosition('tr', 'Top Right')">
									Top Right
								</DropDownMenuItem>
								<DropDownMenuItem class="uppercase" @click="setWatermarkPosition('tl', 'Top Left')">
									Top Left
								</DropDownMenuItem>
								<DropDownMenuItem class="uppercase" @click="setWatermarkPosition('cc', 'Center')">
									Center
								</DropDownMenuItem>
								<DropDownMenuItem class="uppercase" @click="setWatermarkPosition('bl', 'Bottom Left')">
									Bottom Left
								</DropDownMenuItem>
								<DropDownMenuItem class="uppercase" @click="setWatermarkPosition('br', 'Bottom Right')">
									Bottom Right
								</DropDownMenuItem>
							</template>
						</DropDownMenu>
						<FormUpload name="watermark" id="embed-watermark-input" placeholder="No file chosen" type="secondary"
							:label="embedWatermarkTemp ? 'Change' : 'Upload watermark'" :on-file-change="onEmbedPosterPreview"
							show-icon />
						<FormButton v-show="canSaveWatermarkPosition" :disabled="featureProcessing.embedWatermarkPosition"
							@click="saveWatermarkPosition">
							{{ featureProcessing.embedWatermarkPosition ? 'Saving' : 'Save' }}
						</FormButton>
						<FormButton v-if="embedWatermarkTemp" :disabled="featureProcessing.embedWatermark"
							@click="saveEmbedPoster('watermark')">
							{{ featureProcessing.embedWatermark ? 'Saving' : 'Save' }}
						</FormButton>
					</div>
					<Alert v-show="features.embedWatermark.error" class="alert alert-danger">
						{{ features.embedWatermark.error }}
					</Alert>
				</section>
			</template>
		</Collapse>
		<h4 class="text-base font-medium text-surface-text-2 mt-5 mb-2">Playback</h4>
		<section class="grid grid-cols-1 sm:grid-cols-2 grid-rows-[1fr_auto] bg-surface-3 !p-3 !rounded-lg !mb-3">
			<div>
				<h4 class="text-tiny font-medium text-surface-text-2 !mb-1">Autoplay</h4>
				<p class="text-xs text-surface-7">Player starts automatically when embedded</p>
			</div>
			<FormSwitch class="justify-self-start sm:justify-self-end self-center" @change="toggleFeature('embedAutoplay')"
				v-model="features.embedAutoplay.enabled" :disabled="featureProcessing.embedAutoplay" />
		</section>
		<Collapse class="" v-model:is-open="collapseOpen.ga">
			<template #header>
				<h4 class="text-tiny font-medium">Google Analytics</h4>
				<Tooltip wrapper-class="block !ml-2 mr-auto" content="Track player events in your GA dashboard" v-show="!collapseOpen.ga">
					<icon-base class="shrink-0">
						<icon-info />
					</icon-base>
				</Tooltip>
			</template>
			<template #content>
				<div class="flex items-center justify-between gap-x-2 -mt-4 !mb-2 w-full">
					<p class="text-xs text-surface-7">Track player events in your GA dashboard</p>
					<FormSwitch class="justify-self-end" @change="toggleFeature('ga')" v-model="features.ga.enabled"
						@update:modelValue="disabledSaveGA = false" />
				</div>
				<div class="flex flex-col items-stretch gap-2 !mt-1 sm:flex-row sm:items-center" v-if="features.ga.enabled">
					<FormInput customClasses="bg-surface-4" v-model="features.ga.value" placeholder="UA-00000 or G-xxxxx"
						@update:modelValue="disabledSaveGA = false" />
					<FormButton :disabled="!features.ga.value || featureProcessing.ga || disabledSaveGA" @click="savePlayerGAId">
						{{ featureProcessing.ga ? 'Saving' : 'Save' }}
					</FormButton>
				</div>
			</template>
		</Collapse>
	</div>
	<ModalBoxAlert v-model:is-visible="uploadAlertVisible" :message="uploadAlertMessage" />
</template>